import React, { useEffect, useState } from 'react';
import { Button, ProfessionalDateRange } from '../../../shared/components';
import { Filter, Search, Building2, Users, Calendar, ArrowUpDown, X } from 'lucide-react';
import { eventService } from '../../../shared/services';
import { AUDIT_LOG_TYPES } from './auditLogTypes';
import { filterActiveEvents, getCurrentYear } from '../../../shared/utils/eventStatus';

/**
 * Filtros del audit log: alumno, rango de fechas, tipo de movimiento,
 * evento, sede y orden.
 */
const AuditLogsFilters = ({
  filters = {},
  onFiltersChange,
  onClearFilters,
  onSearch,
  loading = false
}) => {
  const [events, setEvents] = useState([]);

  // Cargar eventos para el desplegable
  useEffect(() => {
    let cancelled = false;

    const loadOptions = async () => {
      try {
        const eventsRes = await eventService.getEvents();
        if (cancelled) return;
        setEvents(Array.isArray(eventsRes) ? filterActiveEvents(eventsRes) : []);
      } catch (err) {
        // El desplegable es auxiliar: si falla, los filtros siguen usables
        if (!cancelled) setEvents([]);
      }
    };

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDateRangeChange = (startDate, endDate) => {
    onFiltersChange({ startDate: startDate || '', endDate: endDate || '' });
  };

  const handleChange = (field) => (e) => {
    onFiltersChange({ [field]: e.target.value });
  };

  // El año en curso es el valor por defecto: no cuenta como filtro activo
  const currentYear = getCurrentYear();
  const yearOptions = [0, 1, 2].map((offset) => String(Number(currentYear) - offset));
  if (filters.year && !yearOptions.includes(String(filters.year))) {
    yearOptions.push(String(filters.year));
  }

  const hasActiveFilters =
    (filters.year || '') !== currentYear ||
    filters.studentId ||
    filters.studentName ||
    filters.eventId ||
    filters.auditLogTypes ||
    filters.startDate ||
    filters.endDate ||
    filters.userSede ||
    filters.search;

  return (
    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200/30">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <Filter className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 tracking-tight">
              Filtros del Historial
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Filtrá los movimientos por alumno, fecha, evento o tipo de operación
            </p>
          </div>
        </div>

        {hasActiveFilters && (
          <Button
            onClick={onClearFilters}
            variant="outline"
            size="sm"
            disabled={loading}
            className="text-gray-600"
          >
            <X className="w-4 h-4 mr-2" />
            Limpiar Filtros
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Rango de fechas */}
        <div className="xl:col-span-2">
          <ProfessionalDateRange
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={handleDateRangeChange}
            disabled={loading}
            label="Periodo de Consulta"
            className="w-full"
          />
        </div>

        {/* Alumno */}
        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            Alumno
          </label>
          <input
            type="text"
            value={filters.studentName || ''}
            onChange={(e) =>
              // Escribir un nombre reemplaza el filtro exacto del deep-link
              onFiltersChange({ studentName: e.target.value, studentId: '' })
            }
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            placeholder="Buscar por nombre..."
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>

        {/* Tipo de movimiento */}
        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <ArrowUpDown className="w-4 h-4 inline mr-1" />
            Tipo de movimiento
          </label>
          <select
            value={filters.auditLogTypes || ''}
            onChange={handleChange('auditLogTypes')}
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
          >
            <option value="">Todos los movimientos</option>
            {AUDIT_LOG_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Evento */}
        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Evento
          </label>
          <select
            value={filters.eventId || ''}
            onChange={handleChange('eventId')}
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
          >
            <option value="">Todos los eventos</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.title}
              </option>
            ))}
          </select>
        </div>

        {/* Sede */}
        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building2 className="w-4 h-4 inline mr-1" />
            Sede
          </label>
          <select
            value={filters.userSede || ''}
            onChange={handleChange('userSede')}
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
          >
            <option value="">Todas las sedes</option>
            <option value="Belgrano">Belgrano</option>
            <option value="Caballito">Caballito</option>
            <option value="Palermo">Palermo</option>
          </select>
        </div>

        {/* Año */}
        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Año
          </label>
          <select
            value={filters.year || ''}
            onChange={handleChange('year')}
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
            <option value="">Todos los años</option>
          </select>
        </div>

        {/* Orden */}
        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <ArrowUpDown className="w-4 h-4 inline mr-1" />
            Orden
          </label>
          <select
            value={filters.sortOrder || 'DESC'}
            onChange={handleChange('sortOrder')}
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
          >
            <option value="DESC">Más recientes primero</option>
            <option value="ASC">Más antiguos primero</option>
          </select>
        </div>

        {/* Buscar en la descripción */}
        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Search className="w-4 h-4 inline mr-1" />
            Detalle
          </label>
          <input
            type="text"
            value={filters.search || ''}
            onChange={handleChange('search')}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            placeholder="Buscar en el detalle..."
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <Button
          onClick={onSearch}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Search className="w-4 h-4 mr-2" />
          {loading ? 'Buscando...' : 'Aplicar Filtros'}
        </Button>
      </div>
    </div>
  );
};

export default AuditLogsFilters;
